import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	OverrideContainerComponent,
	Store,
} from 'react-sweet-state';
import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';

type NetworkStatus = {
	isCollabOffline: boolean;
};

export const initialNetworkStatus: NetworkStatus = {
	isCollabOffline: false,
};

export const actions = {
	setIsCollabOffline:
		(isCollabOffline: boolean) =>
		({ setState }: { setState: (state: Partial<NetworkStatus>) => void }): void => {
			setState({
				isCollabOffline,
			});
		},
};

type NetworkStatusContainerProps = {
	initialNetworkStatus?: Partial<NetworkStatus>;
};

export const networkStatusStore: Store<
	{
		isCollabOffline: boolean;
	},
	{
		setIsCollabOffline: (
			isCollabOffline: boolean,
		) => ({ setState }: { setState: (state: Partial<NetworkStatus>) => void }) => void;
	}
> = createStore({
	initialState: {
		...initialNetworkStatus,
	},
	actions,
	name: 'networkStatus',
});

// Browser-based online/offline detection proved unreliable (some VPN/browser combinations report
// false positives), so every public read API from this store permanently reports online/no-op
// rather than risk incorrectly blocking users who are actually online. This is the single place
// that encodes that decision; consumers should keep reading `isCollabOffline` from this hook
// rather than hardcoding `false` themselves.
export const useCollabNetworkStatus: HookFunction<
	{
		isCollabOffline: boolean;
	},
	BoundActions<
		{
			isCollabOffline: boolean;
		},
		{
			setIsCollabOffline: (
				isCollabOffline: boolean,
			) => ({ setState }: { setState: (state: Partial<NetworkStatus>) => void }) => void;
		}
	>,
	void
> = createHook(networkStatusStore, {
	selector: () => ({ isCollabOffline: false }),
});

export const useCollabNetworkStatusActions: HookActionsFunction<
	BoundActions<
		{
			isCollabOffline: boolean;
		},
		{
			setIsCollabOffline: (
				isCollabOffline: boolean,
			) => ({ setState }: { setState: (state: Partial<NetworkStatus>) => void }) => void;
		}
	>
> = createActionsHook(networkStatusStore);

export const NetworkStatusContainer: OverrideContainerComponent<NetworkStatusContainerProps> =
	createContainer<NetworkStatus, typeof actions, NetworkStatusContainerProps>(networkStatusStore, {
		displayName: 'NetworkStatusContainer',
		onInit:
			() =>
			({ setState }, { initialNetworkStatus }) => {
				if (initialNetworkStatus) {
					setState({
						...initialNetworkStatus,
					});
				}
			},
	});

/**
 * Get the current offline status outside of React components
 *
 * @returns true if offline, false if online
 */
export const getIsCollabOffline = (): boolean => {
	return false;
};

/**
 * Subscribe to network status changes outside of React components
 *
 * @param _listener - Unused; status changes never occur (see Critical invariants in AGENTS.md)
 * @returns Unsubscribe function
 */
export const subscribeToCollabNetworkStatus = (_listener: () => void): (() => void) => {
	return () => {};
};
